import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Skeleton } from '../../ui/Skeleton';
import type { AttentionGroup } from '../attention/useAttention';
type Props = {
  groups: AttentionGroup[];
  loading: boolean;
};

/**
 * De werklijst van het overzicht: drie bakjes (Nu · Deze week · Winkel op orde), elk een rustige
 * lijst. Rechts in elke rij staat wat ze gaat doen ("Inpakken"), zodat de lijst leest als taken
 * en niet als meldingen. Lege bakjes verdwijnen; alles leeg = één regel dat alles bij is.
 */
export function AttentionList({
  groups,
  loading
}: Props) {
  const filled = groups.filter(g => g.items.length > 0);
  if (loading) return <div role="status" aria-label="Werklijst laden" className="space-y-3" data-id="element-1612" data-mp="355tvycifl3hi">
        <Skeleton className="h-5 w-24 rounded" data-id="element-1613" data-mp="353r8o08nxogh" />
        <Skeleton className="h-16 w-full rounded-lg" data-id="element-1614" data-mp="6gn1a9meterl" />
        <Skeleton className="h-16 w-full rounded-lg" data-id="element-1615" data-mp="1ov9xwvfj50e" />
      </div>;
  if (filled.length === 0) return <div className="flex items-center gap-3 rounded-lg border border-line bg-white px-5 py-8 text-[15px] text-ink" data-id="element-1616" data-mp="sp2h0m49ls1j">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sage/15 text-sage" aria-hidden="true" data-id="element-1617" data-mp="xo5b5bipiduq">
          <CheckIcon className="h-4 w-4" strokeWidth={2} data-id="element-1618" data-mp="jvz3kdtmdf6w" />
        </span>
        Alles is bij. Tijd voor je cliënten.
      </div>;
  return <div className="space-y-8" data-id="element-1619" data-mp="9ykq8n2zt6it">
      {filled.map(g => <section key={g.key} aria-labelledby={`attention-${g.key}`} data-id="element-1620" data-mp="9wzvoxgohnky">
          <div className="mb-2 flex items-baseline justify-between gap-4" data-id="element-1621" data-mp="eyfe0hrh1fzn">
            <h2 id={`attention-${g.key}`} className="text-[15px] font-medium text-ink" data-id="element-1622" data-mp="y4nxgxzk5fj">
              {g.title}
              <span className="ml-2 font-normal tabular-nums text-muted" data-id="element-1623" data-mp="4yg9qx234bsl">{g.items.length}</span>
            </h2>
            <p className="hidden text-[13px] text-muted sm:block" data-id="element-1624" data-mp="1vcywnbnsjy1z">{g.hint}</p>
          </div>
          <ul className={`divide-y divide-line overflow-hidden rounded-lg border bg-white ${g.key === 'nu' ? 'border-clay/40' : 'border-line'}`} data-id="element-1625" data-mp="1zc7r6iqnnzyl">
            {g.items.map(t => <li key={t.key} data-id="element-1626" data-mp="33al2vkh8lnrw">
                <Link to={t.to} className="group flex items-center gap-4 px-4 py-3.5 transition-colors duration-150 ease-soft hover:bg-shell sm:px-5" data-id="element-1627" data-mp="339ji2oaf5mwz">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${g.key === 'nu' ? 'bg-blush/70 text-clay' : 'bg-sand text-ink'}`} aria-hidden="true" data-id="element-1628" data-mp="2uaod1fj42iv9">
                    <t.icon className="h-4 w-4" strokeWidth={1.75} data-id="element-1629" data-mp="3mzlu5fbl69mf" />
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-1630" data-mp="3i72fef15x8g1">
                    <span className="block truncate text-[14px] text-ink" data-id="element-1631" data-mp="3d9agv260io2v">{t.title}</span>
                    {t.meta && <span className="block truncate text-[13px] text-muted" data-id="element-1632" data-mp="ej0ghl80escz">{t.meta}</span>}
                  </span>
                  <span className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-[13px] text-muted transition-colors duration-150 ease-soft group-hover:text-ink sm:inline-flex" data-id="element-1633" data-mp="9s0n2py3msf8">
                    {t.action}
                    <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-1634" data-mp="m3ktne9ynyt5" />
                  </span>
                  <ArrowRightIcon className="h-4 w-4 shrink-0 text-muted sm:hidden" strokeWidth={1.5} aria-hidden="true" data-id="element-1635" data-mp="v4iwvucks2ao" />
                  <span className="sr-only" data-id="element-1636" data-mp="102apyroyhugg">, {t.action}</span>
                </Link>
              </li>)}
          </ul>
        </section>)}
    </div>;
}